import {rootState} from '@mintdata-wsp/common/state/RootState';
import {controllers} from '@mintdata-wsp/wsp/controllers/WspControllers';
import {PublishDialogKind} from '@mintdata-wsp/wsp/ui/dialogs/publish/PublishDialog';
import * as is from 'is_js';
import bind from 'bind-decorator';
import {SurfaceKeyboardController} from './surface/SurfaceKeyboardController';
import {MeshKeyboardController} from '../spreadsheet/mesh/MeshKeyboardController';

export enum Shortcuts
{
    /**
     * Common
     */

    COPY = 'commonCopy',
    CUT = 'commonCut',
    PASTE = 'commonPaste',
    UNDO = 'commonUndo',
    REDO = 'commonRedo',

    /**
     * Designer
     */

    DESIGNER_TOGGLE_PAGES_PANEL = '_togglePagesPanel',
    DESIGNER_TOGGLE_PROPERTIES_PANEL = '_togglePropertiesPanel',
    DESIGNER_TOGGLE_SPREADSHEET = '_toggleSpreadsheet',
    DESIGNER_TOGGLE_SPREADSHEET_FULLSCREEN = '_toggleSpreadsheetFullScreen',
    DESIGNER_SHOW_ALL_PANELS = '_showAllPanels',
    DESIGNER_HIDE_ALL_PANELS = '_hideAllPanels',
    DESIGNER_UNDO_CHANGE = '_undoChange',
    DESIGNER_REDO_CHANGE = '_redoChange',
    DESIGNER_NAME_CURRENT_VERSION = '_nameCurrentVersion',
    DESIGNER_TOGGLE_GRID_LINES = '_toggleGridlines',
    DESIGNER_TOGGLE_VERSIONS_VIEW = '_toggleVersionsView',
    DESIGNER_TOGGLE_PREVIEW_MODE = '_togglePreviewMode',
    DESIGNER_TOGGLE_ZEN_PREVIEW_MODE = '_toggleZenPreviewMode',
    DESIGNER_TOGGLE_TOOLBAR = '_toggleToolbar',
    DESIGNER_TOGGLE_COMMAND_MENU = '_toggleCommandMenu',
    DESIGNER_ZOOM_IN = '_zoomIn',
    DESIGNER_ZOOM_OUT = '_zoomOut',    
    DESIGNER_RESET_ZOOM_AND_PAN = '_resetZoomAndPan',
    DESIGNER_ZOOM_TO_FIT = '_zoomToFit',
    DESIGNER_ZOOM_TO_200 = '_zoomTo200',
    DESIGNER_ZOOM_TO_50 = '_zoomTo50',
    DESIGNER_ZOOM_TO_25 = '_zoomTo25',
    DESIGNER_OPEN_FLOW_EDITOR = '_openFlowEditor',
    DESIGNER_OPEN_PUBLISH_DIALOG = '_openPublishDialog',
    DESIGNER_DUPLICATE_DESIGN = '_duplicateDesign',
    DESIGNER_RENAME_DESIGN = '_renameDesign',
    DESIGNER_ENABLE_COMPONENT_REPARENTING = '_enableComponentReparentingFromPanel',
    DESIGNER_DISABLE_COMPONENT_REPARENTING = '_disableComponentReparentingFromPanel',

    /**
     * SurfaceKeyboardController
     */

    SURFACE_ENABLE_PAN = '_enablePan',
    SURFACE_DISABLE_PAN = '_disablePan',
    SURFACE_ENABLE_BOX_REPARENTING = '_enableBoxReparenting',
    SURFACE_DISABLE_BOX_REPARENTING = '_disableBoxReparenting',
    SURFACE_ESCAPE_BOX_REPARENTING = '_escapeBoxReparenting',
    SURFACE_GROUP_INTO_BOX = '_groupIntoBox',
    SURFACE_UNGROUP_FROM_BOX = '_ungroupFromBox',
    SURFACE_COPY_BOXES = '_copyBoxes',
    SURFACE_PASTE_BOXES = '_pasteBoxes',
    SURFACE_REMOVE_SELECTED_COMPONENTS = '_removeSelectedComponents',
    SURFACE_MOVE_SELECTED_COMPONENTS = '_moveSelectedComponents',
    SURFACE_RESIZE_SELECTED_COMPONENTS = '_resizeSelectedComponents',
    SURFACE_ENABLE_DRAWING_COMPONENT = '_enableDrawingComponent',
    SURFACE_ESCAPE_DRAWING_COMPONENT = '_escapeDrawingComponent',

    /**
     * MeshKeyboardController
     */
    
     SPREADSHEET_MOVE_ACTIVE_CELL_LEFT = 'moveActiveCellLeft',
     SPREADSHEET_MOVE_ACTIVE_CELL_UP = 'moveActiveCellUp',
     SPREADSHEET_MOVE_ACTIVE_CELL_RIGHT = 'moveActiveCellRight',
     SPREADSHEET_MOVE_ACTIVE_CELL_DOWN = 'moveActiveCellDown',
     SPREADSHEET_EDIT_ACTIVE_CELL = 'startEditingCell',
     SPREADSHEET_EDIT_OR_CREATE_NOTE = 'startEditingOrCreateNote',
     SPREADSHEET_CREATE_NAMED_RANGE = 'createNamedRanges',
     SPREADSHEET_TOGGLE_NAMED_RANGE_VISIBILITY = 'toggleNamedRangesVisibility',
     SPREADSHEET_ENTER = 'stopBindingOrStartEditingOrMoveActiveCell',
     SPREADSHEET_OPEN_NESTED_FRAME = 'enterNestedFrameView',
     SPREADSHEET_CLOSE_NESTED_FRAME = 'exitDeepestNestedFrameView',
     SPREADSHEET_CLEAR_SELECTED_CELLS = 'clearSelectedCells',
     SPREADSHEET_CLEAR_SELECTED_CELL_VALUES = 'clearSelectedCellValues',
     SPREADSHEET_TOGGLE_SELECTED_CELL_LAZINESS = 'toggleSelectedCellsLaziness',
     SPREADSHEET_EXTEND_SELECTION_LEFT = 'extendSelectionLeft',
     SPREADSHEET_EXTEND_SELECTION_UP = 'extendSelectionUp',
     SPREADSHEET_EXTEND_SELECTION_RIGHT = 'extendSelectionRight',
     SPREADSHEET_EXTEND_SELECTION_DOWN = 'extendSelectionDown',
     SPREADSHEET_TOGGLE_FONT_BOLD = 'changeCellFontToBold',
     SPREADSHEET_MASK_VALUE = 'maskCellValue',
     SPREADSHEET_TEXT_WRAP= 'toggleTextWrappingClip',
     SPREADSHEET_SELECT_ALL = 'selectAll',
     SPREADSHEET_EXECUTE_LAZY_CELL = 'executeLazyCell',
     SPREADSHEET_STOP_CELL_EVALUATION = 'stopCellEvaluation',
     SPREADSHEET_RESET = 'reset',
     SPREADSHEET_MOVE_ACTIVE_CELL_TO_NEAREST_VALUE_LEFT = 'moveActiveCellToImportantLeft',
     SPREADSHEET_MOVE_ACTIVE_CELL_TO_NEAREST_VALUE_UP = 'moveActiveCellToImportantUp',
     SPREADSHEET_MOVE_ACTIVE_CELL_TO_NEAREST_VALUE_RIGHT = 'moveActiveCellToImportantRight',
     SPREADSHEET_MOVE_ACTIVE_CELL_TO_NEAREST_VALUE_DOWN = 'moveActiveCellToImportantDown',
     SPREADSHEET_EXTEND_SELECTION_TO_NEAREST_VALUE_LEFT = 'extendSelectionToImportantLeft',
     SPREADSHEET_EXTEND_SELECTION_TO_NEAREST_VALUE_UP = 'extendSelectionToImportantUp',
     SPREADSHEET_EXTEND_SELECTION_TO_NEAREST_VALUE_RIGHT = 'extendSelectionToImportantRight',
     SPREADSHEET_EXTEND_SELECTION_TO_NEAREST_VALUE_DOWN = 'extendSelectionToImportantDown',
     SPREADSHEET_MODE = 'mode',
     SPREADSHEET_PASTE_AS_PLAIN_TEXT = 'pasteAsPlainText',
};

export const COMMON_KEY_MAP =
    {
        commonCut: 'mod+x',
        commonCopy: 'mod+c',
        commonPaste: 'mod+v',
        commonUndo: 'mod+z',
        commonRedo: 'mod+shift+z',
    };

export class KeyboardController
{
    private static OS_KEY_MAPS: any = {
        mod: {
            default: 'Ctrl',
            mac: '⌘',
        },
        alt: {
            default: 'Alt',
            mac: '⌥',
        },
        option: {
            default: 'Alt',
            mac: '⌥',
        },
        shift: {
            default: 'Shift',
            mac: '⇧',
        },
        ctrl: {
            default: 'Ctrl',
            mac: '^',
        },
        plus: {
            default: 'Plus',
            mac: '+'
        },
        '-': {
            default: 'Minus',
            mac: '-'
        }
    }

    public static readonly KEY_MAP =
        {
            _togglePagesPanel: ['alt+1'],
            _togglePropertiesPanel: ['alt+2'],
            _toggleSpreadsheet: 'alt+3',
            _toggleSpreadsheetFullScreen: 'alt+f',
            _showAllPanels: 'alt+4',
            _hideAllPanels: 'alt+0',
            _undoChange: 'mod+z',
            _redoChange: 'mod+shift+z',
            _nameCurrentVersion: 'alt+s',
            _toggleGridlines: 'alt+g',
            _toggleVersionsView: 'alt+h',
            _togglePreviewMode: 'alt+w',
            _toggleZenPreviewMode: 'alt+a',
            _toggleToolbar: 'alt+`',
            _toggleCommandMenu: 'mod+shift+f',
            _zoomIn: ['mod+shift+plus', 'mod+shift+='],
            _zoomOut: ['mod+shift+-', 'mod+shift+_'],
            _resetZoomAndPan: 'mod+0',
            _zoomToFit: 'mod+1',
            _zoomTo200: 'mod+alt+1',
            _zoomTo50: 'mod+alt+2',
            _zoomTo25: 'mod+alt+3',
            _openFlowEditor: 'alt+e',
            _openPublishDialog: 'alt+p',
            _duplicateDesign: 'mod+d',
            _renameDesign: 'shift+f6',
            _enableComponentReparentingFromPanel: {sequence: 'mod', action: 'keydown'},
            _disableComponentReparentingFromPanel: {sequence: 'mod', action: 'keyup'}
        };

    public static readonly KEY_HANDLERS = {};

    public static readonly SHORTCUT_LABELS_MAP = {};

    private static readonly SHORTCUT_LABEL_OVERRIDE = {
        _zoomIn: KeyboardController._parseShortcut('mod+shift+plus'),
    };

    public static ignoreTags = (handler:(event:KeyboardEvent) => void, tags?:string[]) => (event:KeyboardEvent) =>
    {
        const target = event.target as HTMLInputElement;

        tags = tags || ['input', 'textarea', 'select'];

        if(
            tags.indexOf(target.nodeName.toLowerCase()) !== -1
            || target.isContentEditable
        )
            return;

        handler(event);
    };

    @bind
    public init():void
    {
        this._initKeyHandlersMap();
        this.addShortcutLabels(COMMON_KEY_MAP);
        this.addShortcutLabels(KeyboardController.KEY_MAP);
        this.addShortcutLabels(SurfaceKeyboardController.KEY_MAP);
        this.addShortcutLabels(MeshKeyboardController.KEY_MAP);

        for( const overrideName in KeyboardController.SHORTCUT_LABEL_OVERRIDE )
            KeyboardController.SHORTCUT_LABELS_MAP[overrideName] = KeyboardController.SHORTCUT_LABEL_OVERRIDE[overrideName];

        // convinient snippet for local development - opens properties panel at the start
        // const {openDesign, workbenchVisibility} = controllers.ui;
        // const interval = setInterval(() =>
        // {
        //     if( openDesign.getDesignState() &&
        //         openDesign.getDesignState().surface &&
        //         openDesign.getDesignState().surface.activePage &&
        //         openDesign.getDesignState().surface.activePage.artboard &&
        //         openDesign.getDesignState().surface.activePage.artboard.viewport )
        //     {
        //         clearInterval(interval);
        //         this._resetView();
        //         workbenchVisibility.togglePropertiesPanelVisibility();
        //     }
        // }, 500);
    }

    @bind
    public getKeyMap():any
    {
        return KeyboardController.KEY_MAP;
    }

    @bind
    public getKeyHandlers():any
    {
        return KeyboardController.KEY_HANDLERS;
    }

    private _initKeyHandlersMap()
    {
        for( const funcName in KeyboardController.KEY_MAP )
        {
            const keyHandler = this[funcName];

            if( !keyHandler )
                console.error(`Could not find keyboard handler for ${funcName}`);

            KeyboardController.KEY_HANDLERS[funcName] = KeyboardController.ignoreTags(keyHandler);
        }
    }

    public addShortcutLabels(keyMap:any)
    {
        for( const funcName in keyMap )
        {
            if( KeyboardController.SHORTCUT_LABELS_MAP[funcName] )
                continue;

            const shortcut:any = keyMap[funcName];

            if( typeof shortcut === 'string' )
                KeyboardController.SHORTCUT_LABELS_MAP[funcName] = KeyboardController._parseShortcut(shortcut);
            
            if( shortcut instanceof Array )
            {
                const shortcutLabels = [];

                for( const item of shortcut )
                    shortcutLabels.push(KeyboardController._parseShortcut(item));

                KeyboardController.SHORTCUT_LABELS_MAP[funcName] = shortcutLabels.join(', ');
            }

            if( shortcut.sequence )
            {
                // TODO: How to display 'keyup' and 'keydown' things?
                KeyboardController.SHORTCUT_LABELS_MAP[funcName] = KeyboardController._parseShortcut(shortcut.sequence);
            }
        }
    }
    
    public getShortcutLabel(shortcutName: Shortcuts):string
    {
        return KeyboardController.SHORTCUT_LABELS_MAP[shortcutName] || '';
    }

    private static _detectOS() {
        if (is.mac())
            return 'mac';
        if (is.windows())
            return 'windows';
        if (is.linux())
            return 'linux';
        return 'default'
    }

    private static _parseShortcut(shortcut:string):string|JSX.Element
    {
        const os = this._detectOS();

        if( os === 'mac' )
        {
            // OSX & Windows historically use different key order
            // Usually, 'Ctrl' is the first mod key on Windows, but 'Cmd' is the last mod key on Mac)
            shortcut = shortcut.replace('mod+shift', 'shift+mod');
            shortcut = shortcut.replace('mod+alt', 'alt+mod');
        }

        const keys = shortcut.split('+');

        const keyLabels = [];

        for( let key of keys )
        {
            const keyMap = KeyboardController.OS_KEY_MAPS[key];

            if( keyMap )
                if( keyMap[os] )
                    keyLabels.push(keyMap[os]);
                else
                    keyLabels.push(keyMap['default']);
            else
            {
                key = key.replace(/^(.)/g, ($1) => $1.toUpperCase());

                keyLabels.push(key);
            }
        }

        return keyLabels.join(os === 'mac' ? '' : '+');
    }

    private _togglePropertiesPanel = () =>
    {
        controllers.ui.workbenchVisibility.togglePropertiesPanelVisibility();
    };

    private _togglePagesPanel = () =>
    {
        controllers.ui.workbenchVisibility.togglePagesPanelVisibility();
    };

    private _toggleSpreadsheet = () =>
    {
        controllers.ui.workbenchVisibility.toggleSpreadsheetVisibility();
    };

    private _toggleSpreadsheetFullScreen = (event:KeyboardEvent) =>
    {
        const {sampleDesign} = rootState.ui.visualDesigner;

        if( sampleDesign && !sampleDesign.showSurface )
            return;

        const {workbenchVisibility} = controllers.ui;

        workbenchVisibility.toggleSpreadsheetFullScreen();
        event.preventDefault();
    };

    private _showAllPanels = () =>
    {
        const {workbenchVisibility, topMenu} = controllers.ui;

        const isPagesPanelVisible:boolean = workbenchVisibility.isPagesPanelVisible();
        const isPropertiesPanelVisible:boolean = workbenchVisibility.isPropertiesPanelVisible();
        const isSpreadsheetVisible:boolean = workbenchVisibility.isSpreadsheetVisible();

        if( isPagesPanelVisible && isPropertiesPanelVisible && isSpreadsheetVisible )
            topMenu.hideAllPanelsHandler();
        else
            topMenu.showAllPanelsHandler();
    };

    private _hideAllPanels = () =>
    {
        const {workbenchVisibility, topMenu} = controllers.ui;

        topMenu.hideAllPanelsHandler();
    };

    private _toggleCommandMenu = (event:KeyboardEvent) =>
    {
        controllers.ui.commandMenu.toggleCommandMenuVisibility();

        event.stopPropagation();
        event.preventDefault();
    };

    private _undoChange = (event:KeyboardEvent) =>
    {
        controllers.ui.topMenu.undoHandler();

        event.stopPropagation();
        event.preventDefault();
    };

    private _redoChange = (event:KeyboardEvent) =>
    {
        controllers.ui.topMenu.redoHandler();

        event.stopPropagation();
        event.preventDefault();
    };

    private _zoomIn = (keyEvent:KeyboardEvent) =>
    {
        keyEvent.preventDefault();

        controllers.ui.topMenu.zoomInHandler();
    };

    private _zoomOut = (keyEvent:KeyboardEvent) =>
    {
        keyEvent.preventDefault();

        controllers.ui.topMenu.zoomOutHandler();
    };

    private _resetZoomAndPan = () =>
    {
        controllers.ui.topMenu.resetZoomAndPanHandler();
    };

    private _zoomToFit = (keyEvent:KeyboardEvent) =>
    {
        keyEvent.preventDefault();

        controllers.ui.topMenu.zoomToFitHandler();
    };

    private _zoomTo200 = (keyEvent:KeyboardEvent) =>
    {
        keyEvent.preventDefault();

        controllers.ui.topMenu.getZoomPercentHandler(200)();
    };

    private _zoomTo50 = (keyEvent:KeyboardEvent) =>
    {
        keyEvent.preventDefault();

        controllers.ui.topMenu.getZoomPercentHandler(50)();
    };

    private _zoomTo25 = (keyEvent:KeyboardEvent) =>
    {
        keyEvent.preventDefault();

        controllers.ui.topMenu.getZoomPercentHandler(25)();
    };

    private _nameCurrentVersion = (keyEvent:KeyboardEvent) =>
    {
        keyEvent.preventDefault();

        controllers.ui.topMenu.nameCurrentVersionHandler();
    };

    private _toggleGridlines = (keyEvent:KeyboardEvent) =>
    {
        keyEvent.preventDefault();

        controllers.ui.topMenu.toggleGridlinesHandler();
    };

    private _toggleVersionsView = (keyEvent:KeyboardEvent) =>
    {
        keyEvent.preventDefault();

        controllers.ui.topMenu.showHideVersionHistoryHandler();
    };

    private _openPublishDialog = (keyEvent:KeyboardEvent) =>
    {
        keyEvent.preventDefault();

        const {openDesign, publish} = controllers.ui;

        const _openDesign = openDesign.getOpenDesign();

        if( !_openDesign ) return;

        publish.showPublishDialog({
            designRef: _openDesign.designRef,
            kind: PublishDialogKind.Full
        }, true);
    };

    private _togglePreviewMode = (keyEvent:KeyboardEvent) =>
    {
        keyEvent.preventDefault();

        controllers.ui.surfaceArea.togglePreviewMode();
    };

    private _toggleZenPreviewMode = (keyEvent:KeyboardEvent) =>
    {
        keyEvent.preventDefault();

        controllers.ui.surfaceArea.toggleZenPreviewMode();
    };

    private _toggleToolbar = (keyEvent:KeyboardEvent) =>
    {
        keyEvent.preventDefault();

        const {topMenu} = controllers.ui;

        topMenu.setTopMenuIsExpanded(!topMenu.isTopMenuExpanded());
    };

    private _newDesign = (keyEvent:KeyboardEvent) =>
    {
        keyEvent.preventDefault();

        const {topMenu} = controllers.ui;

        topMenu.newDesignHandler();
    };

    private _duplicateDesign = (keyEvent:KeyboardEvent) =>
    {
        keyEvent.preventDefault();

        const {topMenu} = controllers.ui;

        topMenu.duplicateDesignHandler();
    };

    private _renameDesign = (keyEvent:KeyboardEvent) =>
    {
        keyEvent.preventDefault();

        const {topMenu} = controllers.ui;

        topMenu.renameDesignHandler();
    };

    private _openFlowEditor = (keyEvent:KeyboardEvent) =>
    {
        keyEvent.preventDefault();

        const {topMenu} = controllers.ui;

        topMenu.flowEditorHandler();
    };

    private _enableComponentReparentingFromPanel = () =>
    {
        const {surfaceGlass} = controllers.ui;

        if( surfaceGlass.isDraggingComponent() )
            surfaceGlass.enableReparenting();

    };

    private _disableComponentReparentingFromPanel = () =>
    {
        const {surfaceGlass} = controllers.ui;

        if( surfaceGlass.isReparentingComponent() )
            surfaceGlass.disableReparenting();
    };
}
